import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Paper, Stack } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import GlobalWrapper from '../../components/Wrapper/GlobalWrapper';
import GlobalTable from '../../components/CustomTableWithPagination/GlobalTable';
import GlobalDialog from '../../components/mui/Dialogue/GlobalDialog';
import { AddButton } from '../../components/mui/Buttons/Buttons';
import { getIntervalCommunities } from '../../redux/features/intervalCommunitySlice';
import { IntervalCommunityTableHeader } from './components/IntervalCommunityTableHeader';
import IntervalCommunityAddEdit from './components/IntervalCommunityAddEdit';
import DeleteIntervalCommunity from './components/DeleteIntervalCommunity';

const IntervalCommunity = () => {
  const dispatch = useDispatch();
  const { data, meta, loading } = useSelector((state) => state.intervalCommunity.table);

  const [tableState, setTableState] = useState({
    page: 1,
    pageSize: 10,
    search: '',
    sort: { field: '', direction: 'asc' },
    checkedRowIds: [],
    frozenColumns: [],
    hiddenColumns: [],
  });

  const [modalState, setModalState] = useState({ actionKey: '', row: null });

  // Params the LIST endpoint understands.
  const dispatchParams = useMemo(
    () => ({
      page: tableState.page,
      limit: tableState.pageSize,
      search: tableState.search || '',
      sortBy: tableState.sort?.field || undefined,
      sortDirection: tableState.sort?.direction || 'asc',
    }),
    [tableState.page, tableState.pageSize, tableState.search, tableState.sort],
  );

  useEffect(() => {
    dispatch(getIntervalCommunities(dispatchParams));
  }, [dispatch, dispatchParams]);

  const handleRefresh = useCallback(() => {
    dispatch(getIntervalCommunities({ ...dispatchParams, hardRefresh: true }));
  }, [dispatch, dispatchParams]);

  // Close a modal; refetch when a mutation succeeded.
  const closeModal = useCallback(
    (shouldRefresh = false) => {
      setModalState({ actionKey: '', row: null });
      if (shouldRefresh) handleRefresh();
    },
    [handleRefresh],
  );

  const headers = useMemo(() => IntervalCommunityTableHeader(), []);

  const handleButtonActionClick = useCallback((actionKey, row) => {
    if (!actionKey || !row) return;
    setModalState({ actionKey, row });
  }, []);

  const modalConfigs = useMemo(
    () => [
      {
        actionKey: 'addCommunity',
        title: 'Add Community',
        render: () => <IntervalCommunityAddEdit onClose={closeModal} />,
      },
      {
        actionKey: 'editCommunity',
        title: 'Edit Community',
        render: ({ row }) => <IntervalCommunityAddEdit row={row} onClose={closeModal} />,
      },
      {
        actionKey: 'deleteCommunity',
        title: 'Delete Community',
        render: ({ row }) => <DeleteIntervalCommunity row={row} onClose={closeModal} />,
      },
    ],
    [closeModal],
  );

  const activeConfig = useMemo(
    () => modalConfigs.find((config) => config.actionKey === modalState.actionKey),
    [modalConfigs, modalState.actionKey],
  );

  return (
    <GlobalWrapper title={'Interval Community'}>
      <Box mt={1}>
        <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
          <AddButton action={() => setModalState({ actionKey: 'addCommunity', row: null })}>
            Add Community
          </AddButton>
        </Stack>

        <Paper elevation={3}>
          <Box mt={1}>
            <GlobalTable
              rowUniqueId="community_id"
              headers={headers}
              tableState={tableState}
              setTableState={setTableState}
              loading={loading}
              rows={data}
              meta={meta}
              onButtonActionClick={handleButtonActionClick}
              exportUrl="interval-community"
              exportParams={{ ...dispatchParams, page: 1, limit: 'no' }}
            />
          </Box>

          <GlobalDialog
            title={activeConfig?.title}
            open={Boolean(modalState?.actionKey)}
            setOpen={(val) => {
              if (!val) setModalState({ actionKey: '', row: null });
            }}
            closeButton={true}
            content={activeConfig?.render ? activeConfig.render({ row: modalState.row }) : null}
            maxWidth={'sm'}
            fullWidth={true}
          />
        </Paper>
      </Box>
    </GlobalWrapper>
  );
};

export default IntervalCommunity;
